import {
    CLUB_API_BASE_URL_LOCAL,
    ClubRegisterResponse,
    DOI_ERROR_CODE,
    LOGIN_API,
    ResponseError,
    SIGNUP_API,
} from 'SharedHelpers/LoginRegisterFormAPI';
import { apiPost, CallFetchOptions, FetchResponse } from 'SharedHelpers/apiFetch';
import { OnFailureData, OnSuccess, ValidationErrors } from 'Interfaces/Api';
import { LoginFormValues, SignupFormValues } from 'Interfaces/LoginRegisterFormProps';

export default class SalesApi {
    private readonly country: string;

    private readonly baseUrl: string;

    private readonly env: string;

    public constructor(country: string, clubBffBaseUrl: string, env: string) {
        this.country = country;
        this.env = env;
        this.baseUrl =
            env === 'local' || window.location.host.includes('review')
                ? CLUB_API_BASE_URL_LOCAL
                : `${clubBffBaseUrl}/auth/`;
    }

    private readonly apiPostSignupLogin = async <E = {}>(
        url: string,
        body: string,
        options: Exclude<CallFetchOptions, 'body'> = {}
    ): Promise<FetchResponse<ClubRegisterResponse>> =>
        apiPost<ClubRegisterResponse, E>(url, body, {
            headers: this._getHeaders(),
            ...options,
        });

    private _getHeaders = (headers: object = {}): Headers =>
        new Headers({ 'Content-Type': 'application/json; charset=utf-8', ...headers });

    private _getSignupUrl = () => `${this.baseUrl}${SIGNUP_API.URL}?country=${this.country}`;

    private _getLoginUrl = () => `${this.baseUrl}${LOGIN_API.URL}?country=${this.country}`;

    // TODO Move to helpers
    public getValidationErrors = (error: ResponseError): ValidationErrors =>
        (error?.validation_errors || {}) as ValidationErrors;

    // TODO Move to helpers
    public isDoiRequired = (error?: ResponseError): boolean => !!error && error.code === DOI_ERROR_CODE;

    private _isErrorResponse = (apiResponse: FetchResponse<ClubRegisterResponse>) => {
        const {
            data: { success, errors },
        } = apiResponse;
        return success === false || (errors && Object.keys(errors).length > 0);
    };

    private _getUrlParams = (formValues: SignupFormValues | LoginFormValues): string => {
        if (this.env === 'production') {
            return JSON.stringify(formValues);
        }
        const params = Object.assign(formValues, { nd: '1' });
        return JSON.stringify(params);
    };

    private _register = async (formValues: SignupFormValues): Promise<FetchResponse<ClubRegisterResponse>> =>
        this.apiPostSignupLogin(this._getSignupUrl(), this._getUrlParams(formValues));

    private _login = async (formValues: LoginFormValues): Promise<FetchResponse<ClubRegisterResponse>> =>
        this.apiPostSignupLogin(this._getLoginUrl(), this._getUrlParams(formValues));

    public registerUser = async (
        formValues: SignupFormValues,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ClubRegisterResponse | {}>
    ): Promise<void> => {
        try {
            const apiResponse = await this._register(formValues);
            if (!this._isErrorResponse(apiResponse)) {
                onSuccess();
                return;
            }
            onFailureData(apiResponse.data);
        } catch (e) {
            onFailureData({});
        }
    };

    public loginUser = async (
        formValues: LoginFormValues,
        onSuccess: OnSuccess,
        onFailureData: OnFailureData<ClubRegisterResponse | {}>
    ): Promise<void> => {
        try {
            const apiResponse = await this._login(formValues);
            if (!this._isErrorResponse(apiResponse)) {
                onSuccess();
                return;
            }
            onFailureData(apiResponse.data);
        } catch (e) {
            onFailureData({});
        }
    };
}
